import { eventChannel, END, EventChannel } from 'redux-saga';
import {
  call,
  CallEffect,
  put,
  PutEffect,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';

import * as Types from '../actions';
import {
  attachPlugin,
  conferencingClientError,
  disposeFailure,
  disposeSuccess,
  gotLocalStream,
  gotRemoteStream,
  initJanusFailure,
  initJanusSuccess,
  initQBFailed,
  initQBSuccess,
  participantJoined,
  participantLeft,
  toggleMuteVideo,
} from '../actionCreators';
import { StoreState } from '../reducers';
import config from '../config';

export function QBInit(params: Types.QBInitActionParams): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      QB.init(
        params.appId,
        params.authKey,
        params.authSecret,
        params.accountKey,
        params.config
      );
      resolve();
    } catch (e) {
      reject(e.message || e);
    }
  });
}

export function QBDispose(): Promise<void> {
  return new Promise((resolve) => {
    resolve();
  });
}

export function* initQB(
  action: Types.QBInitRequestAction
): Generator<
  | CallEffect<void>
  | PutEffect<Types.QBInitSuccessAction>
  | PutEffect<Types.QBInitFailureAction>
> {
  try {
    yield call(QBInit, action.payload);
    yield put(initQBSuccess());
  } catch (e) {
    yield put(initQBFailed(e.message || e));
  }
}

function* initJanus(action: Types.JanusInitRequestAction) {
  const { region, then } = action.payload;
  try {
    let server: string;
    if (region === 'eu') {
      server = JANUS_URL_EU;
    } else if (region === 'us') {
      server = JANUS_URL_US;
    } else {
      throw new Error(`Unsupported region provided: ${region}`);
    }
    const client = new QBVideoConferencingClient({
      server,
      debug: config.Janus.debug,
      iceServers: config.Janus.iceServers,
    });
    const result = initJanusSuccess(client);
    yield put(result);
    if (then) {
      then(result);
    }
  } catch (e) {
    yield put(initJanusFailure(e.message || e));
  }
}

export function* dispose(): Generator<
  | CallEffect<void>
  | PutEffect<Types.QBDisposeSuccessAction>
  | PutEffect<Types.QBDisposeFailureAction>
> {
  try {
    yield call(QBDispose);
    yield put(disposeSuccess());
  } catch (e) {
    yield put(disposeFailure(e.message || e));
  }
}

function createConferenceClientChannel(
  client: QBVideoConferencingClient
): EventChannel<Types.ConferencingClientEvent> {
  return eventChannel((emitter) => {
    client.on(client.events.LOCAL_STREAM, (stream) =>
      emitter(gotLocalStream(stream))
    );
    client.on(client.events.REMOTE_STREAM, (stream, userId) => {
      emitter(gotRemoteStream(stream, userId));
    });
    client.on(client.events.PARTICIPANT_JOINED, (publisherInfo) => {
      emitter(participantJoined(publisherInfo));
    });
    client.on(client.events.PARTICIPANT_LEFT, (userId) => {
      emitter(participantLeft(userId));
    });
    client.on(client.events.ERROR, (error) =>
      emitter(conferencingClientError(error))
    );
    client.on(client.events.SESSION_DESTROYED, () => emitter(END));
    return () => {
      Object.values(client.events).map((event) =>
        client.removeAllListeners(event)
      );
    };
  });
}

function isParticipantJoinedAction(
  action: Types.ConferencingClientEvent
): action is Types.ParticipantJoinedEvent {
  return action && action.type && action.type === Types.PARTICIPANT_JOINED;
}

function isLocalStreamEvent(
  action: Types.ConferencingClientEvent
): action is Types.LocalStreamEvent {
  return action && action.type && action.type === Types.LOCAL_STREAM;
}

function isRemoteStreamEvent(
  action: Types.ConferencingClientEvent
): action is Types.RemoteStreamEvent {
  return action && action.type && action.type === Types.REMOTE_STREAM;
}

function* watchConferencingClientEvents(action: Types.JanusInitSuccessAction) {
  const { client } = action.payload;
  const channel: EventChannel<Types.ConferencingClientEvent> = yield call(
    createConferenceClientChannel,
    client
  );
  let muteLocalVideoWhenJoin = true;
  while (true) {
    try {
      const action: Types.ConferencingClientEvent = yield take(channel);
      yield put(action);
      if (isParticipantJoinedAction(action)) {
        const currentUserId: number = yield select(({ auth }: StoreState) =>
          auth.user ? auth.user.id : -1
        );
        if (action.payload.id !== currentUserId) {
          yield put(
            attachPlugin({
              isRemote: true,
              userId: action.payload.id,
            })
          );
        }
      } else if (isLocalStreamEvent(action)) {
        if (muteLocalVideoWhenJoin) {
          yield put(toggleMuteVideo());
          muteLocalVideoWhenJoin = false;
        }
        const el = document.querySelector<HTMLVideoElement>('video#local');
        const { stream } = action.payload;
        if (el && stream) {
          QBVideoConferencingClient.attachMediaStream(el, stream);
        }
      } else if (isRemoteStreamEvent(action)) {
        const { userId, stream } = action.payload;
        const el = document.querySelector<HTMLVideoElement>(
          `video#remote${userId}`
        );
        if (el && stream) {
          QBVideoConferencingClient.attachMediaStream(el, stream);
        }
      }
    } catch (e) {
      yield put({
        type: 'CONFERENCE_CLIENT_CHANNEL_ERROR',
        error: e.message || e,
      });
    }
  }
}

export default [
  takeEvery(Types.QB_INIT_REQUEST, initQB),
  takeEvery(Types.JANUS_INIT_REQUEST, initJanus),
  takeEvery(Types.JANUS_INIT_SUCCESS, watchConferencingClientEvents),
  takeEvery(Types.QB_DISPOSE_REQUEST, dispose),
];
